import { PORTAL_URL } from 'src/constants/externalUrls';
import { primaryDomainHostname } from 'src/constants/hostnameConsts';
import { UrlUtils } from 'src/utils';

/**
 * Where a "Powered by Assembly" badge was shown. It is reported as
 * `utm_content`, so marketing can tell the surfaces apart in one campaign.
 */
export type BadgePlacement =
  | 'client_login'
  | 'client_signup'
  | 'invoice'
  | 'email_footer'
  | 'landing_page';

const BADGE_LANDING_PATH = '/powered-by';
const UTM_SOURCE = 'assembly';
const UTM_MEDIUM = 'powered_by';
const UTM_CAMPAIGN = 'badge';

// Each stack links to its own marketing site, so a staging click lands on the
// staging page. The bare domain, since staging has no www host.
const getMarketingSiteUrl = () =>
  `https://${
    UrlUtils.isStagingHost()
      ? primaryDomainHostname.staging
      : primaryDomainHostname.prod
  }`;

interface BadgeUrlInput {
  placement: BadgePlacement;
  /** Portal id of the workspace showing the badge (`portalHeader`). */
  portalId: string;
  workspaceName: string;
  /** The page the badge is rendered on, used by the pre-flag URL only. */
  currentUrl: string;
  enableGrowthLoops: boolean;
}

/**
 * Builds the badge href. With the growth-loops flag off it keeps the historical
 * URL — the marketing site root with the portal's own address as `utm_source` —
 * so rollout is a pure flag flip.
 */
export const getPoweredByBadgeUrl = ({
  placement,
  portalId,
  workspaceName,
  currentUrl,
  enableGrowthLoops,
}: BadgeUrlInput): string => {
  if (!enableGrowthLoops) {
    return `${PORTAL_URL}/?utm_medium=${UTM_MEDIUM}&utm_source=${encodeURIComponent(
      currentUrl,
    )}`;
  }

  const params = new URLSearchParams({
    utm_source: UTM_SOURCE,
    utm_medium: UTM_MEDIUM,
    utm_campaign: UTM_CAMPAIGN,
    utm_content: placement,
    ref: portalId,
    firm: workspaceName,
  });

  return `${getMarketingSiteUrl()}${BADGE_LANDING_PATH}?${params.toString()}`;
};
